// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useBankUploadSectionMeta } from '@client/banks/hooks/use-bank-upload-section-meta';
import type { BankUploadStep } from '@client/banks/types/bank-upload';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface NavigationControlsProps {
  nextStep?: BankUploadStep;
  previousStep?: BankUploadStep;
  nextLabel?: ReactNode;
  previousLabel?: ReactNode;
  nextIcon?: ReactNode;
  previousIcon?: ReactNode;
  nextDisabled?: boolean;
  previousDisabled?: boolean;
  hideNext?: boolean;
  hidePrevious?: boolean;
  onNext?: () => boolean | void | Promise<boolean | void>;
  onPrevious?: () => void;
  form?: string;
  isFormValid?: () => boolean;
  loading?: boolean;
  loadingLabel?: ReactNode;
}

export const useNavigationControls = (props: NavigationControlsProps) => {
  const { navigate, meta, steps } = useStepsData();
  const { markSectionCompleted } = useBankUploadSectionMeta();

  const completeCurrentSectionIfLeaving = useCallback(
    (nextStep: BankUploadStep) => {
      const currentSection = meta?.section;
      const nextSection = steps.get(nextStep)?.meta?.section;

      if (currentSection && nextSection && currentSection !== nextSection) {
        markSectionCompleted(currentSection);
      }
    },
    [markSectionCompleted, meta?.section, steps],
  );

  const onNext = useCallback(() => {
    const run = async () => {
      if (props.hideNext) {
        return;
      }

      if (!props.nextStep && !props.onNext) {
        return;
      }

      const result = props.onNext ? await props.onNext() : undefined;

      if (result !== false && props.nextStep) {
        completeCurrentSectionIfLeaving(props.nextStep);
        navigate(props.nextStep);
      }
    };

    void run();
  }, [completeCurrentSectionIfLeaving, navigate, props]);

  const onPrevious = useCallback(() => {
    if (props.hidePrevious || !props.previousStep) {
      return;
    }

    props.onPrevious?.();
    navigate(props.previousStep);
  }, [navigate, props]);

  const isNextDisabled = useMemo(() => {
    if (props.hideNext) {
      return true;
    }

    if (!props.nextStep && !props.onNext) {
      return true;
    }

    if (props.isFormValid) {
      return !props.isFormValid();
    }

    return props.nextDisabled ?? false;
  }, [props]);

  const isPreviousDisabled = useMemo(() => {
    if (props.hidePrevious || !props.previousStep) {
      return true;
    }

    return props.previousDisabled ?? false;
  }, [props]);

  return {
    ...props,
    onNext,
    onPrevious,
    isNextDisabled,
    isPreviousDisabled,
    hidePrevious: props.hidePrevious ?? false,
    hideNext: props.hideNext ?? false,
  };
};
